import Check from '@mui/icons-material/Check';
import ChevronRight from '@mui/icons-material/ChevronRight';
import FilterList from '@mui/icons-material/FilterList';
import { buttonClasses, paperClasses, SxProps } from '@mui/material';
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import { TicketSource } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { omit, snakeCase } from 'lodash-es';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import AppliedFiltersChips from '~components/Filters/AppliedFiltersChips';
import FilterMenuItem, { FilterMenuOption } from '~components/Filters/FilterMenuItem';
import Menu from '~components/Menu/Menu';
import { SmallButton } from '~components/Order/ordersDispatchStyledComponents';

import TicketsQueryContext from '../TicketsQueryContext/TicketsQueryContext';
import TicketSummaryQueryContext from '../TicketSummaryQueryContext/TicketSummaryQueryContext';

enum TicketAvailableFilter {
  SOURCES = 'sources',
}

interface OrderTicketFilterProps {
  sx?: SxProps;
}

export default function OrderTicketFilter({ sx }: OrderTicketFilterProps) {
  const { filters: appliedFilters, applyFilters } = useContext(TicketsQueryContext);
  const { applyFilters: applySummaryFilters } = useContext(TicketSummaryQueryContext);

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState<TicketAvailableFilter>();

  // Create static options for TicketSource enum
  const sourceOptions: FilterMenuOption[] = useMemo(
    () => [
      {
        label: t('dispatch.tickets_table.source.api'),
        value: TicketSource.API_INTEGRATION,
      },
      {
        label: t('dispatch.tickets_table.source.web'),
        value: TicketSource.WEB,
      },
      {
        label: t('dispatch.tickets_table.source.mobile'),
        value: TicketSource.DRIVER_APP,
      },
      {
        label: t('dispatch.tickets_table.source.agent'),
        value: TicketSource.AGENT,
      },
    ],
    [],
  );

  const ticketQueryFilterKeyByTicketAvailableFilter: {
    [key in TicketAvailableFilter]: keyof typeof appliedFilters;
  } = {
    [TicketAvailableFilter.SOURCES]: 'sources',
  };

  const filtersForChips = omit(appliedFilters, ['fromDate', 'toDate', 'flagged']);
  const hasFiltersForChips = Object.values(filtersForChips).some((filter) => {
    return Array.isArray(filter) ? filter.length > 0 : Boolean(filter);
  });

  const selectedOptions = useMemo(() => {
    if (!selectedFilter) {
      return [];
    }

    const key = ticketQueryFilterKeyByTicketAvailableFilter[selectedFilter];
    const options = appliedFilters[key] ?? [];

    return options as string[];
  }, [selectedFilter, appliedFilters]);

  const handleSelectedOptionsChange = useCallback(
    (options: string[]) => {
      if (selectedFilter) {
        const newSelectedOptions = options.length > 0 ? options : undefined;
        const key = ticketQueryFilterKeyByTicketAvailableFilter[selectedFilter];

        applyFilters({ [key]: newSelectedOptions });
        applySummaryFilters({ [key]: newSelectedOptions });
      }
    },
    [selectedFilter, applyFilters, applySummaryFilters],
  );

  const handleRemoveAllFilters = useCallback(
    (filters: string | string[]) => {
      const filtersArray = Array.isArray(filters) ? filters : [filters];
      const filtersObject = Object.fromEntries(
        filtersArray.map((filter) => [filter, undefined]),
      );

      applyFilters(filtersObject);
      applySummaryFilters(filtersObject);
    },
    [applyFilters, applySummaryFilters],
  );

  useEffect(() => {
    if (!isMenuOpen) {
      setSelectedFilter(undefined);
    }
  }, [isMenuOpen]);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', ...sx }}>
      <Menu
        sx={{ [`& .${paperClasses.root}`]: { width: '250px' } }}
        onOpenStateChanged={setIsMenuOpen}
        menuTrigger={
          <SmallButton
            size="small"
            startIcon={<FilterList />}
            sx={(theme) => ({
              [`&.${buttonClasses.root}`]: {
                bgcolor: 'background.paper',
                color: theme.palette.text.primary,
                border: `solid 1px ${theme.brandV2.colors.treadGray7}`,
                ...(hasFiltersForChips
                  ? {
                      borderBottomLeftRadius: theme.brandV2.borderRadius,
                      borderBottomRightRadius: 0,
                      borderTopLeftRadius: theme.brandV2.borderRadius,
                      borderTopRightRadius: 0,
                    }
                  : { borderRadius: theme.brandV2.borderRadius }),
              },
            })}
          >
            {t('common.filters')}
          </SmallButton>
        }
      >
        {selectedFilter && (
          <FilterMenuItem
            onSelectedOptionsChange={handleSelectedOptionsChange}
            options={sourceOptions}
            selectAllOptionLabel={`${t('dispatch.tickets_table.filters.all_entities', { entity: t(`dispatch.tickets_table.filters.${snakeCase(selectedFilter)}`) })}`}
            selectedOptions={selectedOptions}
          />
        )}

        {!selectedFilter &&
          Object.values(TicketAvailableFilter).map((filter) => (
            <MenuItem
              key={filter}
              onClick={() => setSelectedFilter(filter)}
              data-ui={`page-dispatch__tickets-table-filter__${filter}-filter-button`}
            >
              <Box
                sx={{
                  alignItems: 'center',
                  display: 'flex',
                  justifyContent: 'space-between',
                  width: '100%',
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={(theme) => ({ color: theme.brandV2.colors.treadBlack })}
                >
                  {t(`dispatch.tickets_table.filters.${snakeCase(filter)}`)}
                </Typography>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {Boolean(
                    appliedFilters[ticketQueryFilterKeyByTicketAvailableFilter[filter]],
                  ) && (
                    <Check
                      sx={(theme) => ({
                        color: theme.brandV2.colors.treadOrange,
                        fontSize: '16px',
                      })}
                    />
                  )}

                  <ChevronRight sx={{ fontSize: '16px' }} />
                </Box>
              </Box>
            </MenuItem>
          ))}
      </Menu>
      <AppliedFiltersChips
        appliedFilters={filtersForChips}
        collectedData={{
          sources: sourceOptions,
        }}
        onRemoveAllFilters={handleRemoveAllFilters}
        onRemoveFilter={handleRemoveAllFilters}
      />
    </Box>
  );
}
